import { queryOptions, useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface PlatformStats {
  vehiclesSold: number;
  verifiedDealers: number;
  activeListings: number;
  avgRating: number;
  totalReviews: number;
  listingsAddedToday: number;
  listingsRecentCount: number;
  listingsRecentDays: number;
  leadsThisWeek: number;
  leadsRecentCount: number;
  leadsRecentDays: number;
  dealersActiveThisWeek: number;
}

/**
 * Fetches live platform statistics from the database.
 * Cached for 5 minutes to avoid excessive queries.
 */
export const platformStatsQueryOptions = () =>
  queryOptions({
    queryKey: ['platform-stats'],
    queryFn: async (): Promise<PlatformStats> => {
      const { data, error } = await supabase.rpc('get_homepage_stats' as any);

      const baseEmpty: PlatformStats = {
        vehiclesSold: 200, verifiedDealers: 0, activeListings: 0, avgRating: 0, totalReviews: 0,
        listingsAddedToday: 0, listingsRecentCount: 0, listingsRecentDays: 0,
        leadsThisWeek: 0, leadsRecentCount: 0, leadsRecentDays: 0, dealersActiveThisWeek: 0,
      };

      if (error || !data) {
        // Fallback: count directly so SSR never renders 0 when the RPC fails.
        const [{ count: activeCount }, { count: dealerCount }] = await Promise.all([
          supabase.from('listings_v2').select('*', { count: 'exact', head: true }).eq('status', 'active'),
          supabase.from('dealer_profiles').select('*', { count: 'exact', head: true }).eq('verified', true),
        ]);
        if (!activeCount && !dealerCount) throw error ?? new Error('platform stats unavailable');
        return {
          ...baseEmpty,
          activeListings: activeCount ?? 0,
          verifiedDealers: dealerCount ?? 0,
        };
      }

      const stats = data as any;
      const realSold = stats.vehiclesSold ?? 0;
      const active = stats.activeListings ?? 0;
      const HISTORICAL_BASELINE = 200;
      const vehiclesSold = Math.max(HISTORICAL_BASELINE, realSold + Math.floor(active * 0.2));

      const today = stats.listingsAddedToday ?? 0;

      // Fallback: if no listings added today, find the most recent window (2,3,7,14,30 days) that has activity
      let listingsRecentCount = today;
      let listingsRecentDays = 0; // 0 means "today"
      if (today === 0) {
        for (const days of [2, 3, 7, 14, 30]) {
          const since = new Date();
          since.setDate(since.getDate() - days);
          const { count } = await supabase
            .from('listings_v2')
            .select('*', { count: 'exact', head: true })
            .eq('status', 'active')
            .gte('created_at', since.toISOString());
          if ((count ?? 0) > 0) {
            listingsRecentCount = count ?? 0;
            listingsRecentDays = days;
            break;
          }
        }
      }

      const leadsWeek = stats.leadsThisWeek ?? 0;
      // Always show last 30 days for stronger social proof (uses SECURITY DEFINER RPC to bypass RLS)
      let leadsRecentCount = 0;
      const leadsRecentDays = 30;
      const { data: leadsCountData } = await supabase.rpc('get_leads_count_last_30d' as any);
      leadsRecentCount = (leadsCountData as number | null) ?? 0;

      return {
        vehiclesSold,
        verifiedDealers: stats.verifiedDealers ?? 0,
        activeListings: active,
        avgRating: stats.avgRating ?? 0,
        totalReviews: stats.totalReviews ?? 0,
        listingsAddedToday: today,
        listingsRecentCount,
        listingsRecentDays,
        leadsThisWeek: leadsWeek,
        leadsRecentCount,
        leadsRecentDays,
        dealersActiveThisWeek: stats.dealersActiveThisWeek ?? 0,
      };
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

export const usePlatformStats = () => {
  return useQuery(platformStatsQueryOptions());
};
